/**
 * The runner hub (§10.4–10.5), pure: queue, one-runner-at-a-time offers, claims (the commit point), per-run lease
 * epochs, renewals, pre-start releases (capped), expiry, exclusive keys and per-profile capacity. The Workspace DO
 * keeps this state, applies these transitions and turns the returned effects into pushes and outbox rows.
 */
import { LeaseTiming, type LendCandidate, type SecretReachRow } from "@minga/protocol"
import { scrubSecrets } from "./activity"
import { deriveId, sha256Hex } from "./hash"
import { mayRun, profileAccessOf, runAskerOf, sharedIn, shareInProject } from "./multiplayer"
import {
  accountQuotasOf,
  exclusiveKeyFor,
  type HubView,
  match,
  pickRunner,
  type RunForMatch,
  type RunnerForMatch,
  type RunnerPresenceValue,
  rateLimitedOnly,
} from "./placement"
import { loadPenalty, loadSummary, type RunnerLoadTrack } from "./resources"
import type {
  Epoch,
  Executor,
  Ids,
  JobTemplate,
  Lease,
  LeaseRef,
  Placement,
  ProfileId,
  RunError,
  RunId,
  RunKind,
  RunnerId,
  RunPhase,
  StopReason,
  Unmet,
  UserId,
} from "./types"

export { checkFence } from "./decide/runs"

export interface HubRun {
  readonly runId: RunId
  readonly projectId: Ids["ProjectId"] | null
  readonly kind: RunKind
  readonly ownerId: UserId
  readonly placement: Placement
  /** The run's frozen job template (C3), carried by Enqueue and returned with the lease by `claim`. */
  readonly job: JobTemplate
  /** Highest epoch granted (0 = never leased). */
  readonly epoch: number
  readonly prestartReleases: number
  /**
   * Decision 0095: why the last pre-start leases ended (a runner's `Release.detail`, newest last, at most
   * `PRESTART_DETAILS`), so the run that ends at the cap says why. Absent = none (and rows from before 0095).
   */
  readonly prestartDetails?: ReadonlyArray<string>
  /** Decision 0150: why the last pre-start lease ended (the next RecordRunLeased/Waiting says so). Absent = none. */
  readonly prestartReason?: PrestartReason
  readonly status: "queued" | "offered" | "leased"
  readonly offer: {
    readonly offerId: Ids["OfferId"]
    readonly runnerId: RunnerId
    readonly profileId: ProfileId
    readonly expiresAt: number
  } | null
  readonly lease: (Lease & { readonly phase: RunPhase }) | null
  /** Runners that declined or let an offer expire since the last change; skipped until something changes. */
  readonly passed: ReadonlyArray<RunnerId>
  /**
   * Decision 0156: why each runner in `passed` passed, and when. Placement skips a passed runner, so without this a run
   * pinned to it waited with no reason at all (W-129, 14 h). `transient` passes (busy, an expired offer) come back on
   * the runner's next heartbeat with a free slot; the others after `PASS_RETRY_MS`. Absent = none (older rows).
   */
  readonly passedWhy?: ReadonlyArray<PassWhy>
  readonly unmet: ReadonlyArray<Unmet>
  readonly enqueuedAt: number
  /**
   * Wave E (decision 0063): the run's item (a validator's subject item) and that item's lend, from Enqueue and later
   * `LendChanged` (`setLend`). Placement and the claim re-check read the lend through `mayRun`. Absent = none.
   */
  readonly itemId?: Ids["ItemId"] | null
  readonly lentRunnerId?: RunnerId | null
  /**
   * Decision 0128: the person who asked for a turn when not its keeper (`Enqueue.askedBy`). Placement uses the asker's
   * own or a team subscription for it. Absent = the keeper.
   */
  readonly turnAsker?: UserId | null
  /**
   * Decision 0165: the item's check that looks heavy, when nothing routes the run to a runner or profile
   * (`Enqueue.heavyCheck`). Placement then prefers the bigger sandbox among one runner's profiles. Absent = not heavy.
   */
  readonly heavyCheck?: string
  /** Display only (Enqueue.label, fix wave §4.4): the item's ref and title for the runner card (D7 names). */
  readonly label?: {
    readonly taskRef: string | null
    readonly taskTitle: string | null
    /** Settings wave: the temporary teammate ("W-3"). */
    readonly workerRef?: string
  }
}

/** Decision 0156: one runner's pass on a run, as the waiting reason it shows. */
export interface PassWhy {
  readonly runnerId: RunnerId
  readonly unmet: Unmet
  readonly at: number
  readonly transient: boolean
}

/** Decision 0156: a runner that passed on a run for a lasting reason is offered it again after this long. */
export const PASS_RETRY_MS = 30 * 60_000

/** The run with `runnerId` added to its passes, with why (the newest why per runner wins). */
const withPass = (r: HubRun, why: PassWhy): HubRun => ({
  ...r,
  passed: r.passed.includes(why.runnerId) ? r.passed : [...r.passed, why.runnerId],
  passedWhy: [...(r.passedWhy ?? []).filter((p) => p.runnerId !== why.runnerId), why],
})

/** The run without any pass by `runnerId` (it gets the run offered again). */
const withoutPass = (r: HubRun, runnerId: RunnerId): HubRun => {
  const { passedWhy, ...rest } = r
  const left = (passedWhy ?? []).filter((p) => p.runnerId !== runnerId)
  return {
    ...rest,
    passed: r.passed.filter((id) => id !== runnerId),
    ...(left.length > 0 ? { passedWhy: left } : {}),
  }
}

/** The run with no passes at all (a runner record changed, a lend changed). */
const withoutPasses = (r: HubRun): HubRun => {
  const { passedWhy: _why, ...rest } = r
  return { ...rest, passed: [] }
}

/**
 * Decision 0156: the waiting reasons of the runners a run passed (placement skips them and says nothing). For a pinned
 * run only its pin counts. A pass recorded before 0156 has no why: it still names the runner.
 */
const passReasons = (run: HubRun): ReadonlyArray<Unmet> => {
  const pin = run.placement.requires.runner
  return run.passed
    .filter((id) => pin === undefined || id === pin)
    .map(
      (id): Unmet =>
        run.passedWhy?.find((p) => p.runnerId === id)?.unmet ?? {
          _tag: "ceiling_refuses",
          runnerId: id,
          why: "it passed on this run (no reason recorded); it is offered the run again soon",
        },
    )
}

/**
 * Decision 0156: the queued runs `runnerId` passed on that it may be offered again now: a transient pass (busy, an
 * expired offer) when `transientToo`, any pass older than `PASS_RETRY_MS`, and a pass without a recorded why.
 */
export const passesToRetry = (
  h: HubState,
  runnerId: RunnerId,
  now: number,
  transientToo: boolean,
): ReadonlyArray<RunId> =>
  h.runs.flatMap((r) => {
    if (r.status !== "queued" || !r.passed.includes(runnerId)) return []
    const why = r.passedWhy?.find((p) => p.runnerId === runnerId)
    if (!why) return [r.runId]
    return (transientToo && why.transient) || now - why.at >= PASS_RETRY_MS ? [r.runId] : []
  })

/** Decision 0156: forget `runnerId`'s passes on these runs (a heartbeat gives them back). */
export const forgetPasses = (h: HubState, runnerId: RunnerId, runIds: ReadonlyArray<RunId>): HubState => {
  const ids = new Set<string>(runIds)
  return { ...h, runs: h.runs.map((r) => (ids.has(r.runId) ? withoutPass(r, runnerId) : r)) }
}

/** Decision 0156: a runner's Decline (reason and detail), kept as the run's waiting reason. */
export type DeclineWhy = {
  readonly reason: "busy" | "ceiling" | "profile_not_ready" | "exclusive_local" | "shutting_down"
  readonly detail: string | null
}

const declineUnmet = (runnerId: RunnerId, profileId: ProfileId, why: DeclineWhy | undefined): Unmet => {
  const detail = why?.detail ? scrubSecrets(why.detail).trim().slice(0, 200) : ""
  switch (why?.reason) {
    case "busy":
      return { _tag: "profile_at_capacity", runnerId, profileId }
    case "exclusive_local":
      return { _tag: "at_capacity", runnerId }
    case "ceiling":
      return { _tag: "ceiling_refuses", runnerId, why: detail || "its ceiling refuses this run" }
    case "shutting_down":
      return {
        _tag: "profile_not_ready",
        runnerId,
        profileId,
        why: detail || "the runner is stopping or updating",
      }
    case "profile_not_ready":
      return { _tag: "profile_not_ready", runnerId, profileId, why: detail || "it declined the run" }
    default:
      return { _tag: "profile_not_ready", runnerId, profileId, why: "it did not answer the offer in time" }
  }
}

/** Reasons without duplicates, the placement's first (a runner's live state says more than its earlier pass). */
const mergeReasons = (a: ReadonlyArray<Unmet>, b: ReadonlyArray<Unmet>): ReadonlyArray<Unmet> => {
  const runnerOf = (u: Unmet): string | null => ("runnerId" in u && u.runnerId ? u.runnerId : null)
  const named = new Set(a.flatMap((u) => (runnerOf(u) ? [runnerOf(u)] : [])))
  const seen = new Set(a.map((u) => JSON.stringify(u)))
  const out = [...a]
  for (const u of b) {
    const k = JSON.stringify(u)
    const r = runnerOf(u)
    if (seen.has(k) || (r !== null && named.has(r))) continue
    seen.add(k)
    out.push(u)
  }
  return out
}

export interface HubRunner {
  readonly record: RunnerForMatch
  readonly connected: boolean
  readonly lastSeenAt: number | null
  /**
   * Decision 0149: the runner's recent load (heartbeat `load`), kept in the Workspace DO's memory only; never stored.
   * Placement subtracts a soft penalty for it (`loadPenalty`).
   */
  readonly load?: RunnerLoadTrack
}

export interface HubState {
  readonly runs: ReadonlyArray<HubRun>
  readonly runners: ReadonlyArray<HubRunner>
  /**
   * Decision 0141: the workspace's secrets (metadata only) for `requires.secrets`. Not stored with the hub: the
   * Workspace DO puts its secret store's rows here. Absent = unknown (names unchecked).
   */
  readonly secrets?: ReadonlyArray<SecretReachRow>
  /**
   * Decision 0166 §3.6: what each person chose for their work in each project (`choiceKey` → `subscriptionKey`s). Kept
   * by the Workspace DO beside the hub; absent or no entry = nothing chosen.
   */
  readonly choices?: ReadonlyMap<string, ReadonlyArray<string>>
}

export const emptyHub: HubState = { runs: [], runners: [] }

export type HubEffect =
  | {
      readonly _tag: "Offer"
      readonly runnerId: RunnerId
      readonly offerId: Ids["OfferId"]
      readonly runId: RunId
      readonly profileId: ProfileId
      readonly expiresAt: number
    }
  | { readonly _tag: "OfferWithdrawn"; readonly runnerId: RunnerId; readonly offerId: Ids["OfferId"] }
  | {
      readonly _tag: "Revoke"
      readonly runnerId: RunnerId
      readonly lease: LeaseRef
      readonly reason: "cancelled" | "owner_stop" | "reassigned" | "runner_paused"
    }
  /**
   * → Project DO RecordRunWaiting (only when the reasons change). `lendable` (wave E, decision 0063, §15 decision
   * 2(a)): other people's personal runners that could take it if lent to the item; only when there are any.
   */
  | {
      readonly _tag: "RunWaiting"
      readonly runId: RunId
      readonly unmet: ReadonlyArray<Unmet>
      readonly since: number
      readonly lendable?: ReadonlyArray<LendCandidate>
    }
  /** → Project DO RecordRunLeased. */
  | { readonly _tag: "RunLeased"; readonly runId: RunId; readonly epoch: Epoch; readonly executor: Executor }
  /** → Project DO RecordRunStarted (phase running). */
  | { readonly _tag: "RunStarted"; readonly runId: RunId; readonly epoch: Epoch }
  /** → Project DO RecordRunEnded (Final, lease_reaper, or the pre-start cap). */
  | {
      readonly _tag: "RunEnded"
      readonly runId: RunId
      readonly epoch: Epoch
      readonly stopReason: StopReason
      /** Decision 0095: the pre-start cap's reason, from the runners' release details. */
      readonly error?: RunError
    }
  /** → the run's sink (Agent DO or Run DO): refuse this epoch from now on. */
  | {
      readonly _tag: "LeaseClosed"
      readonly runId: RunId
      readonly epoch: Epoch
      readonly reason: "final" | "released" | "expired" | "revoked" | "stale"
    }
  /**
   * → Project DO RecordRunRequeued (decision 0039): the hub took the lease back from a runner without `item_model`
   * and queued the run again; no attempt is counted.
   */
  | {
      readonly _tag: "RunRequeued"
      readonly runId: RunId
      readonly epoch: Epoch
      readonly reason: "runner_outdated"
    }

export type HubResult<A = undefined> = {
  readonly state: HubState
  readonly effects: ReadonlyArray<HubEffect>
  readonly value: A
}

const ONLINE_FRESH_MS = 3 * LeaseTiming.HEARTBEAT_EVERY_MS
const AWAY_MS = 24 * 3_600_000

/** Derived runner presence: online/busy (connected, fresh), away (< 24 h), offline, needs_attention. */
export const runnerPresence = (r: HubRunner, now: number, running: number): RunnerPresenceValue => {
  if (r.record.admin === "revoked") return "offline"
  const m = r.record.manifest
  if (r.connected && r.lastSeenAt !== null && now - r.lastSeenAt <= ONLINE_FRESH_MS) {
    if (
      m.profiles.length > 0 &&
      m.profiles.every((p) => p.readiness._tag !== "ready" && p.readiness._tag !== "quota_exhausted")
    )
      return "needs_attention"
    return running >= m.capacity ? "busy" : "online"
  }
  if (r.lastSeenAt !== null && now - r.lastSeenAt < AWAY_MS) return "away"
  return "offline"
}

const leases = (h: HubState) => h.runs.flatMap((r) => (r.lease ? [r.lease] : []))

/** The view `match` needs, derived from the hub state. */
export const hubView = (h: HubState, now: number): HubView => {
  const runningByProfile = new Map<string, number>()
  const runningByRunner = new Map<RunnerId, number>()
  const exclusiveHeld = new Map<string, RunId>()
  for (const r of h.runs) {
    const l =
      r.lease ??
      (r.offer
        ? {
            runnerId: r.offer.runnerId,
            profileId: r.offer.profileId,
            exclusive: [] as ReadonlyArray<string>,
            runId: r.runId,
          }
        : null)
    if (!l) continue
    runningByProfile.set(
      `${l.runnerId}/${l.profileId}`,
      (runningByProfile.get(`${l.runnerId}/${l.profileId}`) ?? 0) + 1,
    )
    runningByRunner.set(l.runnerId, (runningByRunner.get(l.runnerId) ?? 0) + 1)
    for (const k of l.exclusive) exclusiveHeld.set(exclusiveKeyFor(l.runnerId, k), r.runId)
  }
  const freeSlots = new Map<RunnerId, number>()
  const presence = new Map<RunnerId, RunnerPresenceValue>()
  const loadPenalties = new Map<RunnerId, number>()
  for (const rn of h.runners) {
    const running = runningByRunner.get(rn.record.id) ?? 0
    freeSlots.set(rn.record.id, Math.max(0, rn.record.manifest.capacity - running))
    presence.set(rn.record.id, runnerPresence(rn, now, running))
    const penalty = loadPenalty(loadSummary(rn.load, now))
    if (penalty > 0) loadPenalties.set(rn.record.id, penalty)
  }
  return {
    now,
    runningByProfile,
    freeSlots,
    exclusiveHeld,
    presence,
    ...(loadPenalties.size > 0 ? { loadPenalty: loadPenalties } : {}),
    accountQuotas: accountQuotasOf(h.runners.map((r) => r.record)),
    ...(h.secrets ? { secrets: h.secrets } : {}),
    ...(h.choices ? { choices: h.choices } : {}),
  }
}

const upd = (h: HubState, runId: RunId, f: (r: HubRun) => HubRun | null): HubState => ({
  ...h,
  runs: h.runs.flatMap((r) => {
    if (r.runId !== runId) return [r]
    const n = f(r)
    return n ? [n] : []
  }),
})

const same = (a: ReadonlyArray<Unmet>, b: ReadonlyArray<Unmet>) => JSON.stringify(a) === JSON.stringify(b)

/** What a lend can supply (spec §2.3): a tool, a repo, an agent kind, a profile, or what the machine allows. */
const CAPABILITY: ReadonlySet<Unmet["_tag"]> = new Set<Unmet["_tag"]>([
  "missing_tool",
  "missing_repo",
  "missing_agent",
  "profile_not_ready",
  "profile_out_of_scope",
  "ceiling_refuses",
])

/** A view where `runnerId` is online with every slot free: what the runner could do at all, online or not. */
const idleView = (view: HubView, r: RunnerForMatch): HubView => ({
  now: view.now,
  runningByProfile: new Map(),
  freeSlots: new Map([[r.id, Math.max(1, r.manifest.capacity)]]),
  exclusiveHeld: new Map(),
  presence: new Map([[r.id, "online"]]),
  ...(view.lastSessionOf ? { lastSessionOf: view.lastSessionOf } : {}),
  ...(view.secrets ? { secrets: view.secrets } : {}),
  ...(view.choices ? { choices: view.choices } : {}),
})

/** Decision 0128: an `owner_lock` on one subscription (someone else's personal one), as opposed to a whole machine. */
const subscriptionLock = (u: Unmet): boolean => u._tag === "owner_lock" && u.profileId !== undefined

/**
 * Lend and share candidates for a waiting worker or validator run (wave E, decision 0063, spec §2.3; decision 0128):
 *
 * - **Share:** another person's personal subscription on a runner the principal may use (a team runner, e.g. the
 *   Platform Runner) that would run it if its owner made it Team (`profileId` set, `needs` empty).
 * - **Lend:** other people's personal runners that `match` would accept with the owner lock lifted (judged on what they
 *   can do, online or not, busy or not; never a paused one).
 *
 * Both only when every runner the principal may use (their own and the team's, online or not) fails on a capability or
 * on someone else's personal subscription, or when there is no such runner at all (decision 0128: before, nobody was
 * told). `needs` = the capability reasons. A run that only waits for a machine (one asleep, paused or busy) has none;
 * a run already lent has none.
 *
 * A chat turn asked by someone other than its keeper (0128 addendum, 2026-10-20) gets share candidates only, including
 * the keeper's own login on his personal machine (a profile without an explicit scope): a lend is per item, and a turn
 * has none. The Project DO turns them into a card for the login's owner.
 */
export const lendCandidatesFor = (
  run: RunForMatch,
  runners: ReadonlyArray<RunnerForMatch>,
  view: HubView,
): ReadonlyArray<LendCandidate> => {
  const turn = run.kind === "turn"
  if (!turn && run.kind !== "worker" && run.kind !== "validator") return []
  if (turn && runAskerOf(run) === run.ownerId) return []
  if (!turn && run.lentRunnerId) return []
  // decision 0166 §2: a runner the run is pinned away from is no candidate at all (the walk of 2026-10-28: a lead pinned
  // to her keeper's laptop read the team's Platform Runner as "offline" there, and no share card was ever opened)
  const pin = run.placement.requires.runner
  const usable = runners.filter(
    (r) => r.admin === "active" && mayRun(r, run) && (pin === undefined || r.id === pin),
  )
  const needs: Unmet[] = []
  for (const r of usable) {
    const m = match(run, r, idleView(view, r))
    if (m._tag === "eligible") return []
    const cap = m.reasons.filter((u) => CAPABILITY.has(u._tag))
    const locked = m.reasons.filter(subscriptionLock)
    if (cap.length + locked.length === 0 || cap.length + locked.length !== m.reasons.length) return []
    for (const u of cap) if (!needs.some((n) => JSON.stringify(n) === JSON.stringify(u))) needs.push(u)
  }
  const out: LendCandidate[] = []
  const sorted = [...runners].sort((a, b) => (a.id < b.id ? -1 : 1))
  // decision 0128: a personal subscription on a runner the principal may use, that would run it as Team
  for (const r of sorted) {
    if (r.admin !== "active" || !mayRun(r, run) || (pin !== undefined && r.id !== pin)) continue
    for (const p of r.manifest.profiles) {
      // explicit (every Platform Runner subscription, a login its owner set) or a machine's own login (no entry)
      const access = profileAccessOf(r, p.id)
      if (sharedIn(access, run.projectId) || access.ownerId === runAskerOf(run)) continue
      // decision 0166 §3.2: would it run if its owner shared it in this run's project ("Share in <project>")?
      const shared: RunnerForMatch = {
        ...r,
        profileAccess: { ...r.profileAccess, [p.id]: shareInProject(access, run.projectId) },
        manifest: { ...r.manifest, profiles: [p] },
      }
      if (match(run, shared, idleView(view, shared))._tag !== "eligible") continue
      if (out.some((c) => c.ownerId === access.ownerId && c.profileId !== undefined)) continue
      out.push({ runnerId: r.id, ownerId: access.ownerId, needs: [], profileId: p.id, agent: p.agent })
    }
  }
  // a turn has no item to lend a machine to
  if (turn) return out
  for (const r of sorted) {
    if (r.ownerId === run.ownerId || r.serves === "team") continue
    // what it can do, not whether it is free right now: a busy or asleep machine can still be lent (it runs when free)
    if (match(run, r, idleView(view, r), { ignoreOwnerLock: true })._tag !== "eligible") continue
    out.push({ runnerId: r.id, ownerId: r.ownerId, needs })
  }
  return out
}

/** The workspace's Platform Runner (its manifest's trust is `hosted`). */
const isPlatformRunner = (r: RunnerForMatch): boolean => r.manifest.trust === "hosted"

/**
 * 0128 addendum (2026-10-20): a refused subscription (`owner_lock` with a profile) says whether the workspace has a
 * Platform Runner, so the line offers "connect your own subscription on the Platform Runner" only when there is one.
 */
const withPlatformHint = <M extends ReturnType<typeof pickRunner>>(m: M, hosted: boolean): M =>
  m._tag === "eligible" || !hosted
    ? m
    : {
        ...m,
        reasons: m.reasons.map((u) =>
          u._tag === "owner_lock" && u.profileId !== undefined ? { ...u, platformRunner: true } : u,
        ),
      }

/**
 * Offer every queued run to its best eligible runner (one at a time, 10 s). Runs with no candidate become
 * `waiting{unmet}`; the Project DO hears about it only when the reasons change, with the lend candidates (wave E).
 */
export const schedule = (h0: HubState, now: number): HubResult => {
  let h = h0
  const effects: HubEffect[] = []
  const runners = h.runners.map((r) => r.record)
  const hosted = runners.some(isPlatformRunner)
  for (const run of [...h.runs].sort((a, b) => a.enqueuedAt - b.enqueuedAt)) {
    if (run.status !== "queued") continue
    const view = hubView(h, now)
    const picked = withPlatformHint(pickRunner(toMatch(run), runners, view, new Set(run.passed)), hosted)
    // decision 0156: a runner that passed on the run says why, so a run never waits without a reason
    const m =
      picked._tag === "eligible"
        ? picked
        : { ...picked, reasons: mergeReasons(picked.reasons, passReasons(run)) }
    if (m._tag === "eligible") {
      const offerId = deriveId<Ids["OfferId"]>(
        "ofr",
        now,
        `${run.runId}:${run.epoch}:${run.passed.length}:${m.runnerId}`,
      )
      const expiresAt = now + LeaseTiming.OFFER_TTL_MS
      h = upd(h, run.runId, (r) => ({
        ...r,
        status: "offered",
        offer: { offerId, runnerId: m.runnerId, profileId: m.profileId, expiresAt },
      }))
      effects.push({
        _tag: "Offer",
        runnerId: m.runnerId,
        offerId,
        runId: run.runId,
        profileId: m.profileId,
        expiresAt,
      })
    } else if (run.kind === "turn" && picked._tag === "unmet" && rateLimitedOnly(picked.reasons)) {
      // Incident 2026-10-15: a chat turn that can only wait out a usage limit (hours) ends now, truthfully
      // `rate_limited`; the reasons go to the log first, so the run and the asker's notice can say until when.
      h = upd(h, run.runId, () => null)
      if (!same(run.unmet, m.reasons))
        effects.push({ _tag: "RunWaiting", runId: run.runId, unmet: m.reasons, since: now })
      effects.push({
        _tag: "RunEnded",
        runId: run.runId,
        epoch: run.epoch as Epoch,
        stopReason: "rate_limited",
      })
    } else if (!same(run.unmet, m.reasons)) {
      h = upd(h, run.runId, (r) => ({ ...r, unmet: m.reasons }))
      const lendable = lendCandidatesFor(toMatch(run), runners, view)
      effects.push({
        _tag: "RunWaiting",
        runId: run.runId,
        unmet: m.reasons,
        since: now,
        ...(lendable.length > 0 ? { lendable } : {}),
      })
    }
  }
  return { state: h, effects, value: undefined }
}

/**
 * Re-walk of 2026-10-28 (decision 0166 §2): what a turn like `runId` (the agent's live turn) would wait on if `askerId`
 * asked it: a message of theirs that waits behind that turn. Judged as if every runner were free (the turn ahead's own
 * load is no reason), with the share candidates as a waiting turn gets them. Null when the hub doesn't hold that run (a
 * cloud turn, or it ended) or the asker is its keeper.
 */
export const probeAsk = (
  h: HubState,
  runId: string,
  askerId: string,
  now: number,
): { readonly unmet: ReadonlyArray<Unmet>; readonly lendable: ReadonlyArray<LendCandidate> } | null => {
  const live = h.runs.find((r) => r.runId === runId)
  if (live?.kind !== "turn" || live.ownerId === askerId) return null
  const asked: HubRun = { ...live, turnAsker: askerId as UserId, passed: [], unmet: [] }
  const runners = h.runners.map((r) => r.record)
  const view = hubView(h, now)
  const free: HubView = {
    ...view,
    runningByProfile: new Map(),
    exclusiveHeld: new Map(),
    freeSlots: new Map(runners.map((r) => [r.id, Math.max(1, r.manifest.capacity)])),
  }
  const m = withPlatformHint(pickRunner(toMatch(asked), runners, free), runners.some(isPlatformRunner))
  if (m._tag === "eligible") return { unmet: [], lendable: [] }
  return { unmet: m.reasons, lendable: lendCandidatesFor(toMatch(asked), runners, view) }
}

/** The features every run needs (decision 0039): a runner that reads item ids and v2 frames. */
export const RUN_NEEDS: ReadonlyArray<"item_model"> = ["item_model"]

const toMatch = (r: HubRun): RunForMatch => ({
  placement: r.placement,
  ownerId: r.ownerId,
  kind: r.kind,
  projectId: r.projectId,
  model: r.job.model,
  permission: r.job.permission,
  ...(r.job.byAgent ? { byAgent: r.job.byAgent } : {}),
  ...(r.job.routing ? { routing: r.job.routing } : {}),
  // decision 0039: every run needs `item_model`; K14: a narrowing request needs a runner that understands it
  needs: r.job.access !== undefined ? [...RUN_NEEDS, "access_request"] : RUN_NEEDS,
  // wave E (decision 0063): the item's lend, read by `mayRun`
  ...(r.lentRunnerId ? { lentRunnerId: r.lentRunnerId } : {}),
  // decision 0128: a turn's asker (not its keeper), read by `mayUseProfile`
  ...(r.turnAsker ? { turnAsker: r.turnAsker } : {}),
  // decision 0165: a heavy item's run prefers the bigger sandbox inside a runner
  ...(r.heavyCheck ? { heavyCheck: r.heavyCheck } : {}),
})

/**
 * Wave E (decision 0063): an item's lend changed (`LendChanged`). The named runs carry the new lend; queued ones get
 * another chance on every runner, and the queue is scheduled again. An offered run whose runner may no longer take it
 * is refused at claim; a leased run keeps its lease (the lend decides the next attempt).
 */
export const setLend = (
  h: HubState,
  runIds: ReadonlyArray<RunId>,
  lentRunnerId: RunnerId | null,
  now: number,
): HubResult => {
  const ids = new Set<string>(runIds)
  if (!h.runs.some((r) => ids.has(r.runId))) return { state: h, effects: [], value: undefined }
  const runs = h.runs.map((r) =>
    ids.has(r.runId)
      ? r.status === "queued"
        ? { ...withoutPasses(r), lentRunnerId, unmet: [] }
        : { ...r, lentRunnerId }
      : r,
  )
  return schedule({ ...h, runs }, now)
}

/** Does a manifest's feature list hold everything a run needs (decision 0039)? */
export const runnerCanRunItems = (features: ReadonlyArray<string> | undefined): boolean =>
  RUN_NEEDS.every((f) => (features ?? []).includes(f))

/** Is this registered runner too old for the item model (no `item_model` feature)? Unknown runners are not. */
const outdatedRunner = (h: HubState, runnerId: RunnerId): boolean => {
  const r = h.runners.find((x) => x.record.id === runnerId)
  return r !== undefined && !runnerCanRunItems(r.record.manifest.features)
}

/**
 * Decision 0039: take every lease a runner without `item_model` holds back and queue its run again (Register or Renew
 * from such a runner, e.g. right after the deploy). The run keeps its attempt: the Project DO records
 * `RunRequeued{runner_outdated}`, and the next capable runner starts it at a new epoch.
 */
export const requeueOutdated = (h0: HubState, runnerId: RunnerId, now: number): HubResult => {
  let h = h0
  const effects: HubEffect[] = []
  for (const run of h0.runs) {
    const l = run.lease
    if (!l || l.runnerId !== runnerId) continue
    effects.push(
      { _tag: "LeaseClosed", runId: run.runId, epoch: l.epoch, reason: "revoked" },
      { _tag: "RunRequeued", runId: run.runId, epoch: l.epoch, reason: "runner_outdated" },
    )
    h = upd(h, run.runId, (r) => ({ ...r, status: "queued", lease: null, offer: null, unmet: [] }))
  }
  if (effects.length === 0) return { state: h0, effects: [], value: undefined }
  const s = schedule(h, now)
  return { state: s.state, effects: [...effects, ...s.effects], value: undefined }
}

export const enqueue = (
  h: HubState,
  run: Omit<
    HubRun,
    "status" | "offer" | "lease" | "passed" | "unmet" | "enqueuedAt" | "epoch" | "prestartReleases"
  > & { readonly epoch?: number; readonly prestartReleases?: number },
  now: number,
): HubResult => {
  if (h.runs.some((r) => r.runId === run.runId)) return { state: h, effects: [], value: undefined } // idempotent
  const next: HubState = {
    ...h,
    runs: [
      ...h.runs,
      {
        ...run,
        epoch: run.epoch ?? 0,
        prestartReleases: run.prestartReleases ?? 0,
        status: "queued",
        offer: null,
        lease: null,
        passed: [],
        unmet: [],
        enqueuedAt: now,
      },
    ],
  }
  return schedule(next, now)
}

/** Register / heartbeat / manifest change / disconnect: something changed, so passed runners get another chance. */
export const upsertRunner = (h: HubState, runner: HubRunner, now: number): HubResult => {
  const runners = h.runners.some((r) => r.record.id === runner.record.id)
    ? h.runners.map((r) =>
        r.record.id !== runner.record.id
          ? r
          : // decision 0149: a new manifest or incarnation keeps the load the heartbeats built up
            runner.load || !r.load
            ? runner
            : { ...runner, load: r.load },
      )
    : [...h.runners, runner]
  const runs = h.runs.map((r) => (r.status === "queued" ? withoutPasses(r) : r))
  return schedule({ ...h, runs, runners }, now)
}

export type ClaimError =
  | { readonly _tag: "OfferExpired"; readonly offerId: Ids["OfferId"] }
  | { readonly _tag: "NotEligible"; readonly why: string }
  | { readonly _tag: "StaleIncarnation"; readonly current: number }

/**
 * The commit point: epoch + 1, lease (60 s), exclusive keys taken, all at once. Returns the lease with the run's
 * stored job template, from which the Workspace DO builds and signs the JobSpec.
 */
export const claim = (
  h: HubState,
  args: {
    readonly runnerId: RunnerId
    readonly incarnation: number
    readonly offerId: Ids["OfferId"]
    readonly runId: RunId
  },
  now: number,
): HubResult<
  | { readonly _tag: "ok"; readonly lease: Lease; readonly job: JobTemplate }
  | { readonly _tag: "error"; readonly error: ClaimError }
> => {
  const fail = (error: ClaimError) => ({
    state: h,
    effects: [] as HubEffect[],
    value: { _tag: "error" as const, error },
  })
  const runner = h.runners.find((r) => r.record.id === args.runnerId)
  if (!runner) return fail({ _tag: "NotEligible", why: "unknown runner" })
  if (runner.record.incarnation !== args.incarnation)
    return fail({ _tag: "StaleIncarnation", current: runner.record.incarnation })
  const run = h.runs.find((r) => r.runId === args.runId)
  if (
    run?.status !== "offered" ||
    !run.offer ||
    run.offer.offerId !== args.offerId ||
    run.offer.runnerId !== args.runnerId ||
    run.offer.expiresAt < now
  )
    return fail({ _tag: "OfferExpired", offerId: args.offerId })
  // re-check eligibility of the OFFERED profile (the one the runner accepted and the lease names), without this
  // run's own offer counted against the runner: a profile that logged out or ran out of quota fails the claim
  const offered = run.offer.profileId
  const withoutOffer = upd(h, run.runId, (r) => ({ ...r, status: "queued", offer: null }))
  const onlyOffered = {
    ...runner.record,
    manifest: {
      ...runner.record.manifest,
      profiles: runner.record.manifest.profiles.filter((p) => p.id === offered),
    },
  }
  const m = pickRunner(toMatch(run), [onlyOffered], hubView(withoutOffer, now))
  if (m._tag !== "eligible") {
    const error: ClaimError = { _tag: "NotEligible", why: m.reasons.map((u) => u._tag).join(", ") }
    // wave E (decision 0063): the runner may no longer take this run (it stopped serving the team, or the lend ended
    // between offer and claim): the run goes back to the queue and waits with `owner_lock`. Decision 0166 §3.6: likewise
    // when the asker left that subscription out of their choice since the offer (`not_chosen`)
    if (m.reasons.some((u) => u._tag === "owner_lock" || u._tag === "not_chosen")) {
      const s = schedule(
        upd(withoutOffer, run.runId, (r) => ({ ...r, unmet: [] })),
        now,
      )
      return { state: s.state, effects: s.effects, value: { _tag: "error" as const, error } }
    }
    return fail(error)
  }
  const epoch = (run.epoch + 1) as Epoch
  const lease: Lease & { phase: RunPhase } = {
    runId: run.runId,
    epoch,
    runnerId: runner.record.id,
    runnerIncarnation: args.incarnation,
    profileId: run.offer.profileId,
    grantedAt: now,
    expiresAt: now + LeaseTiming.LEASE_TTL_MS,
    exclusive: run.placement.exclusive,
    phase: "preparing",
  }
  const profile = runner.record.manifest.profiles.find((p) => p.id === lease.profileId)
  const state = upd(h, run.runId, (r) => ({ ...r, status: "leased", offer: null, lease, epoch, unmet: [] }))
  const { phase: _phase, ...plainLease } = lease
  return {
    state,
    effects: [
      {
        _tag: "RunLeased",
        runId: run.runId,
        epoch,
        executor: {
          _tag: "runner",
          runnerId: runner.record.id,
          profileId: lease.profileId,
          agent: profile?.agent ?? "claude",
        },
      },
    ],
    value: { _tag: "ok", lease: plainLease, job: run.job },
  }
}

/**
 * Decline (or let an offer expire): the run moves to the next candidate. Decision 0156: the runner's reason is kept
 * (`passedWhy`), so the wait says why when no other runner takes it. No reason given counts as transient.
 */
export const decline = (h: HubState, offerId: Ids["OfferId"], now: number, why?: DeclineWhy): HubResult => {
  const run = h.runs.find((r) => r.offer?.offerId === offerId)
  const offer = run?.offer
  if (!run || !offer) return { state: h, effects: [], value: undefined }
  const next = upd(h, run.runId, (r) =>
    withPass(
      { ...r, status: "queued", offer: null },
      {
        runnerId: offer.runnerId,
        unmet: declineUnmet(offer.runnerId, offer.profileId, why),
        at: now,
        transient: why === undefined || why.reason === "busy" || why.reason === "exclusive_local",
      },
    ),
  )
  return schedule(next, now)
}

export type RenewResult = { readonly lease: LeaseRef } & (
  | { readonly _tag: "renewed"; readonly expiresAt: number }
  | { readonly _tag: "lost"; readonly reason: "expired" | "stale_epoch" | "revoked" }
)

/**
 * Batched renewals. A heartbeat never extends a lease; only this does. A runner without `item_model` (a reconnect
 * that skipped Register) loses every lease (`revoked`) and its runs are queued again without an attempt (0039).
 */
export const renew = (
  h0: HubState,
  runnerId: RunnerId,
  refs: ReadonlyArray<LeaseRef>,
  now: number,
): HubResult<ReadonlyArray<RenewResult>> => {
  if (outdatedRunner(h0, runnerId)) {
    const r = requeueOutdated(h0, runnerId, now)
    return {
      ...r,
      value: refs.map((ref) => ({ lease: ref, _tag: "lost" as const, reason: "revoked" as const })),
    }
  }
  let h = h0
  const results: RenewResult[] = []
  for (const ref of refs) {
    const run = h.runs.find((r) => r.runId === ref.runId)
    const l = run?.lease
    if (!run || !l) {
      results.push({
        lease: ref,
        _tag: "lost",
        reason: run && run.epoch > ref.epoch ? "stale_epoch" : "revoked",
      })
      continue
    }
    if (l.epoch !== ref.epoch || l.runnerId !== runnerId) {
      results.push({ lease: ref, _tag: "lost", reason: "stale_epoch" })
      continue
    }
    if (l.expiresAt < now) {
      results.push({ lease: ref, _tag: "lost", reason: "expired" })
      continue
    }
    const expiresAt = now + LeaseTiming.LEASE_TTL_MS
    h = upd(h, run.runId, (r) => ({ ...r, lease: { ...l, expiresAt } }))
    results.push({ lease: ref, _tag: "renewed", expiresAt })
  }
  return { state: h, effects: [], value: results }
}

type LiveLease = NonNullable<HubRun["lease"]>

/** The run and its lease, if `ref` names the current lease. */
/** The live lease `ref` names, held by `runnerId` (another runner presenting the same epoch is fenced like a stale one). */
const fenceLease = (
  h: HubState,
  runnerId: RunnerId,
  ref: LeaseRef,
): { readonly run: HubRun; readonly lease: LiveLease } | null => {
  const run = h.runs.find((r) => r.runId === ref.runId)
  return run?.lease && run.lease.epoch === ref.epoch && run.lease.runnerId === runnerId
    ? { run, lease: run.lease }
    : null
}

const staleEpoch = (h: HubState, ref: LeaseRef): HubFenceError => ({
  _tag: "StaleEpoch",
  runId: ref.runId,
  current: h.runs.find((r) => r.runId === ref.runId)?.epoch ?? 0,
})

/** Decision 0095: how many pre-start release details a run keeps (the newest). */
export const PRESTART_DETAILS = 3
const DETAIL_MAX = 400

export type PrestartReason = "ceiling" | "prepare_failed" | "shutdown" | "expired"

/** One pre-start end in words: the runner's name and its detail, else what the reason alone says. */
const prestartLine = (h: HubState, runnerId: RunnerId, reason: PrestartReason, detail: string | null) => {
  const name = h.runners.find((r) => r.record.id === runnerId)?.record.name ?? runnerId
  const said = detail ? scrubSecrets(detail).trim() : ""
  const why = said
    ? said.length > DETAIL_MAX
      ? `${said.slice(0, DETAIL_MAX - 1)}…`
      : said
    : reason === "expired"
      ? "the runner stopped answering while preparing (no detail)"
      : reason === "shutdown"
        ? "the runner shut down while preparing (no detail)"
        : reason === "ceiling"
          ? "the runner's policy ceiling refused the run (no detail)"
          : "preparing failed (the runner sent no detail)"
  return `${name}: ${why}`
}

/**
 * Decision 0095: the error a run ended at the pre-start cap carries: every distinct release detail it kept, so the
 * record says why (`sandbox_error` used to end with `error: null`).
 */
export const prestartError = (
  reason: PrestartReason,
  details: ReadonlyArray<string>,
  times = details.length,
): RunError => {
  const code = reason === "ceiling" ? "policy_refused" : "prepare_failed"
  const lines = [...new Set(details)]
  const message = `${reason === "ceiling" ? "refused" : "could not prepare"} ${times} time${times === 1 ? "" : "s"}: ${lines.join("; ")}`
  return { code, message, fingerprint: sha256Hex(`${code}:${lines.join("\n")}`) as RunError["fingerprint"] }
}

/** End a lease before start: requeue with the next epoch, or end the run once the pre-start cap is reached. */
const endPrestart = (
  h: HubState,
  run: HubRun,
  l: LiveLease,
  reason: PrestartReason,
  now: number,
  detail: string | null = null,
): HubResult => {
  const releases = run.prestartReleases + 1
  const details = [...(run.prestartDetails ?? []), prestartLine(h, l.runnerId, reason, detail)].slice(
    -PRESTART_DETAILS,
  )
  const effects: HubEffect[] = [
    {
      _tag: "LeaseClosed",
      runId: run.runId,
      epoch: l.epoch,
      reason: reason === "expired" ? "expired" : "released",
    },
  ]
  if (releases >= LeaseTiming.MAX_PRESTART_RELEASES) {
    effects.push({
      _tag: "RunEnded",
      runId: run.runId,
      epoch: l.epoch,
      stopReason: reason === "ceiling" ? "policy_refused" : "sandbox_error",
      error: prestartError(reason, details, releases),
    })
    return { state: upd(h, run.runId, () => null), effects, value: undefined }
  }
  const requeued = upd(h, run.runId, (r) => {
    const back: HubRun = {
      ...r,
      status: "queued",
      lease: null,
      prestartReleases: releases,
      prestartDetails: details,
      prestartReason: reason,
      unmet: [],
    }
    // decision 0156: a ceiling release passes this runner with its words, so the wait says why
    return reason === "ceiling"
      ? withPass(back, {
          runnerId: l.runnerId,
          unmet: {
            _tag: "ceiling_refuses",
            runnerId: l.runnerId,
            why: detail ? scrubSecrets(detail).trim().slice(0, 200) : "its ceiling refused the job",
          },
          at: now,
          transient: false,
        })
      : back
  })
  const s = schedule(requeued, now)
  return { state: s.state, effects: [...effects, ...s.effects], value: undefined }
}

export type HubFenceError =
  | { readonly _tag: "StaleEpoch"; readonly runId: RunId; readonly current: number }
  | { readonly _tag: "LeaseExpired"; readonly runId: RunId }

/** Release: only valid while preparing (no attempt is counted). */
export const release = (
  h: HubState,
  runnerId: RunnerId,
  ref: LeaseRef,
  reason: "ceiling" | "prepare_failed" | "shutdown",
  now: number,
  /** Decision 0095: the runner's `Release.detail` (why it could not prepare); kept for the run's end. */
  detail: string | null = null,
): HubResult<HubFenceError | null> => {
  const f = fenceLease(h, runnerId, ref)
  if (!f) return { state: h, effects: [], value: staleEpoch(h, ref) }
  if (f.lease.phase !== "preparing")
    return { state: h, effects: [], value: { _tag: "StaleEpoch", runId: ref.runId, current: f.run.epoch } }
  const r = endPrestart(h, f.run, f.lease, reason, now, detail)
  return { ...r, value: null }
}

/** Report{Phase} / Report{Final}. Final releases the lease and its keys once accepted. */
export const report = (
  h: HubState,
  runnerId: RunnerId,
  ref: LeaseRef,
  body:
    | { readonly _tag: "Phase"; readonly phase: RunPhase }
    | { readonly _tag: "Final"; readonly stopReason: StopReason },
  now: number,
): HubResult<HubFenceError | null> => {
  const f = fenceLease(h, runnerId, ref)
  if (!f) return { state: h, effects: [], value: staleEpoch(h, ref) }
  const { run, lease } = f
  if (lease.expiresAt < now)
    return { state: h, effects: [], value: { _tag: "LeaseExpired", runId: ref.runId } }
  if (body._tag === "Phase") {
    const effects: HubEffect[] =
      lease.phase === "preparing" && body.phase === "running"
        ? [{ _tag: "RunStarted", runId: run.runId, epoch: lease.epoch }]
        : []
    return {
      state: upd(h, run.runId, (r) => ({ ...r, lease: { ...lease, phase: body.phase } })),
      effects,
      value: null,
    }
  }
  const epoch = lease.epoch
  const s = schedule(
    upd(h, run.runId, () => null),
    now,
  ) // capacity and keys freed
  return {
    state: s.state,
    effects: [
      { _tag: "RunEnded", runId: run.runId, epoch, stopReason: body.stopReason },
      { _tag: "LeaseClosed", runId: run.runId, epoch, reason: "final" },
      ...s.effects,
    ],
    value: null,
  }
}

/** Revoke a lease (cancel, owner stop, reassignment, pause). The runner interrupts, then reports Final. */
export const revoke = (
  h: HubState,
  runId: RunId,
  reason: "cancelled" | "owner_stop" | "reassigned" | "runner_paused",
  now: number,
): HubResult => {
  const run = h.runs.find((r) => r.runId === runId)
  if (!run) return { state: h, effects: [], value: undefined }
  if (run.status === "queued" || run.status === "offered") {
    const effects: HubEffect[] = run.offer
      ? [{ _tag: "OfferWithdrawn", runnerId: run.offer.runnerId, offerId: run.offer.offerId }]
      : []
    effects.push({ _tag: "RunEnded", runId, epoch: run.epoch as Epoch, stopReason: "cancelled" })
    return { state: upd(h, runId, () => null), effects, value: undefined }
  }
  const l = run.lease
  if (!l) return { state: h, effects: [], value: undefined }
  void now
  return {
    state: h,
    effects: [{ _tag: "Revoke", runnerId: l.runnerId, lease: { runId, epoch: l.epoch }, reason }],
    value: undefined,
  }
}

/**
 * The alarm: expired offers move on; expired leases are requeued (still preparing) or end the run with
 * `runner_lost` (started). Keys are released and LeaseClosed goes to the sink.
 */
export const expire = (h0: HubState, now: number): HubResult => {
  let h = h0
  const effects: HubEffect[] = []
  for (const run of h0.runs) {
    const offer = run.offer
    if (run.status === "offered" && offer && offer.expiresAt <= now) {
      effects.push({ _tag: "OfferWithdrawn", runnerId: offer.runnerId, offerId: offer.offerId })
      h = upd(h, run.runId, (r) =>
        withPass(
          { ...r, status: "queued", offer: null },
          {
            runnerId: offer.runnerId,
            unmet: declineUnmet(offer.runnerId, offer.profileId, undefined),
            at: now,
            transient: true,
          },
        ),
      )
    }
  }
  for (const run of h.runs) {
    const l = run.lease
    if (!l || l.expiresAt > now) continue
    const r = endLease(h, run, l, now)
    h = r.state
    effects.push(...r.effects)
  }
  const s = schedule(h, now)
  return { state: s.state, effects: [...effects, ...s.effects], value: undefined }
}

/**
 * A lease its runner lost: still preparing → requeued (the pre-start cap applies); started → the run ends
 * `runner_lost` and the sink refuses the epoch.
 */
const endLease = (h: HubState, run: HubRun, l: LiveLease, now: number): HubResult => {
  if (l.phase === "preparing") return endPrestart(h, run, l, "expired", now)
  return {
    state: upd(h, run.runId, () => null),
    effects: [
      { _tag: "RunEnded", runId: run.runId, epoch: l.epoch, stopReason: "runner_lost" },
      { _tag: "LeaseClosed", runId: run.runId, epoch: l.epoch, reason: "expired" },
    ],
    value: undefined,
  }
}

/**
 * Register after a restart: keep leases the hub still holds for this runner (and the process is alive), abort the rest.
 * `features` = the registering manifest's features: a runner without `item_model` keeps nothing (decision 0039); the
 * Workspace DO then calls `requeueOutdated`.
 */
export const registerVerdicts = (
  h: HubState,
  runnerId: RunnerId,
  active: ReadonlyArray<{ readonly lease: LeaseRef; readonly processAlive: boolean }>,
  now: number,
  features?: ReadonlyArray<string>,
): ReadonlyArray<{
  readonly lease: LeaseRef
  readonly verdict: "keep" | "abort"
  readonly expiresAt: number | null
}> =>
  active.map(({ lease, processAlive }) => {
    if (features !== undefined && !runnerCanRunItems(features))
      return { lease, verdict: "abort" as const, expiresAt: null }
    const f = fenceLease(h, runnerId, lease)
    const keep = f !== null && f.lease.expiresAt >= now && processAlive
    return { lease, verdict: keep ? "keep" : "abort", expiresAt: keep && f ? f.lease.expiresAt : null }
  })

/**
 * Register after a restart (sim fixes S10): the verdicts of `registerVerdicts`, and every lease the hub holds for this
 * runner that is not kept (aborted, or missing from what the runner reported) ends at once instead of waiting out the
 * lease TTL: still preparing → requeued; started → `runner_lost`. Then the queue is scheduled again (the capacity is
 * free). An outdated runner (no `item_model`) is the caller's `requeueOutdated` instead.
 */
export const reconcileRegister = (
  h0: HubState,
  runnerId: RunnerId,
  active: ReadonlyArray<{ readonly lease: LeaseRef; readonly processAlive: boolean }>,
  now: number,
  features?: ReadonlyArray<string>,
): HubResult<ReturnType<typeof registerVerdicts>> => {
  const verdicts = registerVerdicts(h0, runnerId, active, now, features)
  const kept = new Set(
    verdicts.filter((v) => v.verdict === "keep").map((v) => `${v.lease.runId}:${v.lease.epoch}`),
  )
  let h = h0
  const effects: HubEffect[] = []
  for (const run of h0.runs) {
    const l = run.lease
    if (!l || l.runnerId !== runnerId || kept.has(`${run.runId}:${l.epoch}`)) continue
    const cur = h.runs.find((r) => r.runId === run.runId)
    if (!cur?.lease) continue
    const r = endLease(h, cur, cur.lease, now)
    h = r.state
    effects.push(...r.effects)
  }
  const s = schedule(h, now)
  return { state: s.state, effects: [...effects, ...s.effects], value: verdicts }
}

/** Earliest offer, lease or known quota-reset deadline for the Workspace DO alarm. */
export const nextHubAlarm = (h: HubState): number | null => {
  let min: number | null = null
  for (const r of h.runs) {
    const resets =
      !r.offer && !r.lease
        ? r.unmet.flatMap((u) => (u._tag === "rate_limited" && u.resetsAt !== null ? [u.resetsAt] : []))
        : []
    const at = r.offer?.expiresAt ?? r.lease?.expiresAt ?? (resets.length ? Math.min(...resets) : null)
    if (at !== null && (min === null || at < min)) min = at
  }
  return min
}

export { leases as hubLeases }
